import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/* Where an inline chip's panel opens (2026-10-14): right under the paragraph,
   list item or reply that holds the chip, never in a pop-up. A block renders
   its own empty slot after its content and provides it; a chip portals its
   open panel (a question card, a review, an action's dialog) into the nearest
   slot. */
export const SlotContext = createContext(null);

// render(slotNode, slotElement): the block draws its content, then the slot.
export function SlotBlock({ render }) {
  const [node, setNode] = useState(null);
  return render(node, <div className="bi-slot" ref={setNode} />);
}

// After opening, the chat scrolls just enough to show the panel, never so far
// that the line with its chip leaves the top (a tall card stops 60px down).
function reveal(el) {
  const log = el?.closest('.buffy-chat-log');
  if (!el || !log) return;
  const lr = log.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const by = Math.min(r.bottom - lr.bottom + 16, r.top - lr.top - 60);
  if (by > 0) log.scrollBy({ top: by, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}

// Eases open on the next frame (so opening animates too); content stays
// mounted once shown, so closing animates as well.
export function Unfold({ open, children }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [seen, setSeen] = useState(open);
  // Shown once it has opened, and again while it grows in the next 2.5s
  // (a question card or a review arrives after its loading line).
  useEffect(() => {
    if (!shown) return undefined;
    const el = ref.current;
    const t = setTimeout(() => reveal(el), 340);
    const ro = typeof ResizeObserver !== 'undefined' && el ? new ResizeObserver(() => reveal(el)) : null;
    const start = setTimeout(() => ro?.observe(el), 360);
    const stop = setTimeout(() => ro?.disconnect(), 2500);
    return () => { clearTimeout(t); clearTimeout(start); clearTimeout(stop); ro?.disconnect(); };
  }, [shown]);
  useEffect(() => {
    if (!open) { setShown(false); return undefined; }
    setSeen(true);
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [open]);
  return <div ref={ref} className={`bi-unfold${shown ? ' is-open' : ''}`} inert={shown ? undefined : ''}>
    <div className="bi-unfold-in"><div className="bi-unfold-pad">{seen && children}</div></div>
  </div>;
}

// A chip's panel, in the slot of the block that holds the chip.
export function InlinePanel({ open, children }) {
  const slot = useContext(SlotContext);
  if (!slot) return null;
  return createPortal(<Unfold open={open}>{children}</Unfold>, slot);
}
